import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram } from "lucide-react";
import { siteConfig, footerLinks } from "@/data/siteData";
import logo from "@/assets/logo-transparent.webp";
import complianceLogos from "@/assets/compliance-logos.png";
import popiBadge from "@/assets/compliance/popi-badge.webp";
import saFlag from "@/assets/africa-flag.png";

export function Footer() {
  return (
    <footer className="bg-foreground text-background">
      <div className="container-main py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <img src={logo} alt="IntelliBPO" className="h-12 w-auto" />
            </Link>
            <p className="text-background/70 text-sm mb-6 leading-relaxed">
              {siteConfig.tagline}
              <br />
              {siteConfig.description}
            </p>
            <div className="flex gap-3 mb-6">
              <a href={siteConfig.social.linkedin} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={siteConfig.social.facebook} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={siteConfig.social.twitter} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary transition-colors" aria-label="X">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <a href={siteConfig.social.instagram} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
            <a href="https://bpo.signal2sale.com/contact" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-secondary to-primary text-primary-foreground text-xs font-semibold uppercase tracking-wide hover:opacity-90 transition-opacity">
              Book a Consultation
            </a>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-base mb-4">Services</h3>
            <ul className="space-y-2.5">
              {footerLinks.services.map((link) =>
              <li key={link.name}>
                  <Link to={link.href} className="text-background/70 hover:text-primary transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="font-semibold text-base mb-4">Contact Us</h3>
            <ul className="space-y-2.5">
              {footerLinks.contact.map((link) =>
              <li key={link.name}>
                  {link.href.startsWith('tel:') || link.href.startsWith('mailto:') ?
                <a href={link.href} className="text-background/70 hover:text-primary transition-colors text-sm">
                      {link.name}
                    </a> :

                <Link to={link.href} className="text-background/70 hover:text-primary transition-colors text-sm">
                      {link.name}
                    </Link>
                }
                </li>
              )}
            </ul>
          </div>

          {/* Compliance */}
          <div>
            <h3 className="font-semibold text-base mb-4">Compliance</h3>
            <div className="flex items-center gap-3 mb-4">
              <img src={complianceLogos} alt="PCI DSS, GDPR, HIPAA Compliance" className="h-18 w-auto object-contain" />
              
            </div>
            <div className="flex items-center gap-2">
              <img src={saFlag} alt="South African Flag" className="h-20 md:h-32 w-auto" />
              <span className="uppercase font-semibold text-xs text-background/70">A Proudly South African Company</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/10">
        <div className="container-main py-4 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-background/50">
          <p>© 2026 IntelliBPO. All Rights Reserved.</p>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex items-center gap-4">
              {footerLinks.legal.map((link) =>
              <Link key={link.name} to={link.href} className="hover:text-primary transition-colors">
                  {link.name}
                </Link>
              )}
            </div>
            {/* Agency credit. Fixed Neelo layout: a bordered pill (the stroke
                is a brand requirement, never remove it), N mark only — the
                text already names Neelo Digital. Colours follow this footer's
                own link tokens (background/70 text, background/20 border,
                primary on hover) because the footer is always dark. */}
            <a
              href="https://neelodigital.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Developed by Neelo Digital (opens in a new tab)"
              className="group inline-flex items-center gap-2 rounded-full border border-background/20 px-3 py-1.5 font-['Inter_Tight',sans-serif] text-[11px] leading-none text-background/70 transition-colors hover:border-primary/40 hover:text-primary"
            >
              <span>
                Developed by <span className="font-medium">Neelo Digital</span>
              </span>
              <img
                src="/brand/neelo-n-white.png"
                alt=""
                aria-hidden="true"
                width={620}
                height={288}
                loading="lazy"
                className="h-3 w-auto opacity-75 transition-opacity group-hover:opacity-100"
              />
            </a>
          </div>

        </div>
      </div>
    </footer>);

}